// FIX: Import `TeamInLeague` and `Tactics` types from `domain/entities`.
import { TeamInLeague, Tactics, Player, TacticalPosition, TrainingFocus } from '../domain/entities';
import { FORMATION_OPTIONS, FORMATIONS, POSITION_MAP } from '../domain/constants';
import { calculateMatchPerformance, calculatePositionalPenalty } from '../utils/utilities';

export const setDefaultTactics = (team: TeamInLeague): void => {
    const formation = FORMATION_OPTIONS[0]; // Default to 4-4-2
    const lineup: Tactics['lineup'] = {} as any;
    const formationPositions = FORMATIONS[formation];

    const availablePlayers = [...team.squad];

    formationPositions.forEach(pos => {
        const requiredGeneralPos = POSITION_MAP[pos];
        
        let bestPlayerIndex = -1;
        let bestSkill = -1;

        availablePlayers.forEach((player, index) => {
            if (player.position === requiredGeneralPos) {
                if (player.skill > bestSkill) {
                    bestSkill = player.skill;
                    bestPlayerIndex = index;
                }
            }
        });
        
        if (bestPlayerIndex !== -1) {
            lineup[pos] = availablePlayers[bestPlayerIndex].id;
            availablePlayers.splice(bestPlayerIndex, 1);
        } else {
            // If no suitable player, try to find any player
            if (availablePlayers.length > 0) {
                 const bestRemaining = availablePlayers.sort((a,b) => b.skill - a.skill)[0];
                 lineup[pos] = bestRemaining.id;
                 availablePlayers.splice(availablePlayers.findIndex(p => p.id === bestRemaining.id), 1);
            } else {
                lineup[pos] = null;
            }
        }
    });

    team.tactics.formation = formation;
    team.tactics.mentality = 'Balanced';
    team.tactics.lineup = lineup;
    team.tactics.attackingFocus = 'Mixed';
    team.tactics.passingStyle = 'Mixed';
    team.tactics.defensiveStyle = 'Standard';
    team.tactics.tacklingStyle = 'Normal';
};

export const updateAILineup = (team: TeamInLeague): void => {
    const { formation, mentality } = team.tactics;
    const formationPositions = FORMATIONS[formation];
    if (!formationPositions) return;

    const newLineup: Tactics['lineup'] = {};
    const availablePlayers = team.squad.filter(p => p.injury?.type !== 'Severe' && !p.suspension);
    const assignedPlayerIds = new Set<number>();

    const getEffectiveSkill = (p: Player) => (p.skill + p.form) * (0.5 + (p.fitness / 200));

    // Step 1: Assign best player for each position
    formationPositions.forEach(pos => {
        const requiredPos = POSITION_MAP[pos];
        const bestPlayer = availablePlayers
            .filter(p => !assignedPlayerIds.has(p.id) && p.position === requiredPos)
            .sort((a, b) => getEffectiveSkill(b) - getEffectiveSkill(a))[0];

        if (bestPlayer) {
            newLineup[pos] = bestPlayer.id;
            assignedPlayerIds.add(bestPlayer.id);
        } else {
            newLineup[pos] = null;
        }
    });

    // Step 2: Fill empty slots with best remaining players
    formationPositions.forEach(pos => {
        if (newLineup[pos] === null) {
            const bestRemaining = availablePlayers
                .filter(p => !assignedPlayerIds.has(p.id))
                .sort((a, b) => getEffectiveSkill(b) - getEffectiveSkill(a))[0];

            if (bestRemaining) {
                newLineup[pos] = bestRemaining.id;
                assignedPlayerIds.add(bestRemaining.id);
            }
        }
    });

    team.tactics.lineup = newLineup;
};

export const updateAITactics = (team: TeamInLeague): void => {
    // Pick best formation based on squad composition
    const positionCounts = { GK: 0, DEF: 0, MID: 0, FWD: 0 };
    team.squad.forEach(p => positionCounts[p.position]++);

    const availableFormations = FORMATION_OPTIONS.filter(f => {
        const positions = FORMATIONS[f];
        const required = { GK: 0, DEF: 0, MID: 0, FWD: 0 };
        positions.forEach(pos => {
            const generalPos = POSITION_MAP[pos];
            required[generalPos]++;
        });
        // Can field this formation if we have at least the required players
        return positionCounts.GK >= required.GK &&
               positionCounts.DEF >= required.DEF &&
               positionCounts.MID >= required.MID &&
               positionCounts.FWD >= required.FWD;
    });

    // Prefer formations that match squad strengths
    const formationScores = availableFormations.map(formation => {
        const positions = FORMATIONS[formation];
        const required = { GK: 0, DEF: 0, MID: 0, FWD: 0 };
        positions.forEach(pos => {
            const generalPos = POSITION_MAP[pos];
            required[generalPos]++;
        });

        // Score based on how well it uses strong positions
        let score = 0;
        const squadByPos = {
            GK: team.squad.filter(p => p.position === 'GK').sort((a, b) => b.skill - a.skill),
            DEF: team.squad.filter(p => p.position === 'DEF').sort((a, b) => b.skill - a.skill),
            MID: team.squad.filter(p => p.position === 'MID').sort((a, b) => b.skill - a.skill),
            FWD: team.squad.filter(p => p.position === 'FWD').sort((a, b) => b.skill - a.skill)
        };

        // Sum the skill of players who would actually play in this formation
        score += squadByPos.DEF.slice(0, required.DEF).reduce((sum, p) => sum + p.skill, 0);
        score += squadByPos.MID.slice(0, required.MID).reduce((sum, p) => sum + p.skill, 0);
        score += squadByPos.FWD.slice(0, required.FWD).reduce((sum, p) => sum + p.skill, 0);

        return { formation, score };
    });

    const bestFormation = formationScores.sort((a, b) => b.score - a.score)[0]?.formation || team.tactics.formation;

    // Update formation if better option found
    if (bestFormation !== team.tactics.formation) {
        team.tactics.formation = bestFormation;
    }

    // Set mentality based on team strength relative to league
    const avgSkill = team.squad.length > 0 ? team.squad.reduce((sum, p) => sum + p.skill, 0) / team.squad.length : 0;
    if (avgSkill > team.baseSkill + 5) {
        team.tactics.mentality = 'Attacking';
    } else if (avgSkill < team.baseSkill - 5) {
        team.tactics.mentality = 'Defensive';
    } else {
        team.tactics.mentality = 'Balanced';
    }

    // Set attacking focus based on wingers
    const leftWingers = team.squad.filter(p =>
        (p.position === 'MID' || p.position === 'FWD') && p.favoredPositionCategory === 'Wide' && p.favoredFoot === 'Left'
    );
    const rightWingers = team.squad.filter(p =>
        (p.position === 'MID' || p.position === 'FWD') && p.favoredPositionCategory === 'Wide' && p.favoredFoot === 'Right'
    );
    const avgLeftWingerSkill = leftWingers.length > 0 ? leftWingers.reduce((sum, p) => sum + p.skill, 0) / leftWingers.length : 0;
    const avgRightWingerSkill = rightWingers.length > 0 ? rightWingers.reduce((sum, p) => sum + p.skill, 0) / rightWingers.length : 0;

    if (avgLeftWingerSkill > avgSkill + 5 && avgLeftWingerSkill > avgRightWingerSkill) {
        team.tactics.attackingFocus = 'Down Left Flank';
    } else if (avgRightWingerSkill > avgSkill + 5 && avgRightWingerSkill > avgLeftWingerSkill) {
        team.tactics.attackingFocus = 'Down Right Flank';
    } else if (avgLeftWingerSkill > avgSkill || avgRightWingerSkill > avgSkill) {
        team.tactics.attackingFocus = 'Mixed';
    } else {
        team.tactics.attackingFocus = 'Through Middle';
    }

    // Set passing style based on midfield creativity
    const midfielders = team.squad.filter(p => p.position === 'MID');
    const avgCreativity = midfielders.length > 0
        ? midfielders.reduce((sum, p) => sum + (p.attributes.creativity || p.skill / 2), 0) / midfielders.length
        : team.baseSkill;

    if (avgCreativity > team.baseSkill + 5) {
        team.tactics.passingStyle = 'Short';
    } else if (avgCreativity < team.baseSkill - 5) {
        team.tactics.passingStyle = 'Direct';
    } else {
        team.tactics.passingStyle = 'Mixed';
    }

    // Set defensive style based on squad fitness/stamina
    const avgFitnessMax = team.squad.reduce((sum, p) => sum + p.fitnessMax, 0) / team.squad.length;
    if (avgFitnessMax > 92 && team.squad.filter(p => p.fitness > 70).length > 14) {
        team.tactics.defensiveStyle = 'High Press';
    } else if (avgSkill < team.baseSkill - 5) {
        team.tactics.defensiveStyle = 'Deep Block';
    } else {
        team.tactics.defensiveStyle = 'Standard';
    }

    // Set tackling style based on discipline (red cards history)
    const recentRedCards = team.squad.reduce((sum, p) => sum + (p.redCards || 0), 0);
    if (recentRedCards > 3) {
        team.tactics.tacklingStyle = 'Cautious';
    } else if (team.tactics.mentality === 'Defensive') {
        team.tactics.tacklingStyle = 'Aggressive';
    } else {
        team.tactics.tacklingStyle = 'Normal';
    }
};

export interface SquadSuggestion {
    reserve: Player;
    starter: Player;
    position: TacticalPosition;
    reserveFinalPerf: number;
    starterFinalPerf: number;
}

/**
 * Calculate an enhanced performance score that includes morale and form stability
 */
const calculateEnhancedPerformance = (player: Player, isCurrentStarter: boolean = false): number => {
    const basePerf = calculateMatchPerformance(player);

    // Morale modifier: Ecstatic(+3), Happy(+1.5), Content(0), Unhappy(-2), Furious(-4)
    const moraleModifiers: Record<string, number> = {
        'Ecstatic': 3,
        'Happy': 1.5,
        'Content': 0,
        'Unhappy': -2,
        'Furious': -4
    };

    const moraleBonus = moraleModifiers[player.morale] || 0;

    // Stability bonus for current starters in good form/morale to discourage unnecessary swaps
    // This encourages team consistency which is important for real football
    let stabilityBonus = 0;
    if (isCurrentStarter && player.morale !== 'Unhappy' && player.morale !== 'Furious' && player.form >= 0) {
        // Bonus ranges from 0.5 (for content/happy) to 1.0+ (for ecstatic with good form)
        stabilityBonus = 0.5 + (Math.max(0, player.form) * 0.1);

        // Additional bonus for playing multiple consecutive games (team cohesion)
        const consecutiveGames = getConsecutiveGameAppearances(player);
        if (consecutiveGames >= 2) {
            // +0.2 for 2 games, +0.3 for 3+, up to +0.5 for 5+ games
            stabilityBonus += Math.min(0.5, consecutiveGames * 0.1);
        }
    }

    return basePerf + moraleBonus + stabilityBonus;
};

/**
 * Count consecutive games a player has appeared in (from recent ratings)
 */
const getConsecutiveGameAppearances = (player: Player): number => {
    if (!player.recentRatings || player.recentRatings.length === 0) return 0;

    // Count ratings from most recent backwards - each rating = 1 game played
    let consecutive = 0;
    for (let i = player.recentRatings.length - 1; i >= 0; i--) {
        if (player.recentRatings[i] > 0) {
            consecutive++;
        } else {
            break; // Gap in appearances
        }
    }
    return consecutive;
};

export const getSquadSelectionSuggestions = (team: TeamInLeague, gameState?: any): SquadSuggestion[] => {
    const { squad, tactics } = team;
    const { formation, lineup } = tactics;

    // Calculate the optimal lineup using the same algorithm as "Best XI"
    const formationPositions = FORMATIONS[formation];
    if (!formationPositions) return [];

    const availablePlayers = squad.filter(p => p.injury?.type !== 'Severe' && !p.suspension);
    const optimalLineup: Record<TacticalPosition, Player | null> = {};
    const assignedPlayerIds = new Set<number>();

    // Helper function to calculate final performance for a position (same as Best XI)
    const calculateFinalPerformance = (player: Player, position: TacticalPosition, isCurrentStarter: boolean = false): number => {
        const enhancedPerf = calculateEnhancedPerformance(player, isCurrentStarter);
        const penalty = calculatePositionalPenalty(player, position);
        return enhancedPerf * (1 - penalty);
    };

    // Build optimal lineup using greedy global optimization (same as Best XI)
    formationPositions.forEach(pos => {
        let bestPlayer: Player | null = null;
        let bestScore = -Infinity;

        const requiredPosition = POSITION_MAP[pos];

        availablePlayers.forEach(player => {
            if (assignedPlayerIds.has(player.id)) return;

            // Only consider players whose general position matches the required position
            if (player.position !== requiredPosition) return;

            const finalPerf = calculateFinalPerformance(player, pos);
            if (finalPerf > bestScore) {
                bestScore = finalPerf;
                bestPlayer = player;
            }
        });

        if (bestPlayer) {
            optimalLineup[pos] = bestPlayer;
            assignedPlayerIds.add(bestPlayer.id);
        } else {
            optimalLineup[pos] = null;
        }
    });

    // Compare current lineup with optimal lineup to generate suggestions
    const suggestions: SquadSuggestion[] = [];
    const currentLineupIds = new Set(Object.values(lineup).filter(id => id !== null));

    formationPositions.forEach(pos => {
        const currentPlayerId = lineup[pos];
        const optimalPlayer = optimalLineup[pos];

        // If position has different player in optimal lineup, suggest the change
        if (optimalPlayer && currentPlayerId !== optimalPlayer.id) {
            const currentPlayer = currentPlayerId ? squad.find(p => p.id === currentPlayerId) : null;

            // Only suggest if the current player exists and optimal player is actually better
            if (currentPlayer) {
                const currentPerf = calculateFinalPerformance(currentPlayer, pos, true);  // Current starter gets stability bonus
                const optimalPerf = calculateFinalPerformance(optimalPlayer, pos, false);

                // Only suggest if improvement is meaningful (> 1.2 points to avoid noise and unnecessary swaps)
                if (optimalPerf - currentPerf > 1.2) {
                    suggestions.push({
                        reserve: optimalPlayer,
                        starter: currentPlayer,
                        position: pos,
                        reserveFinalPerf: optimalPerf,
                        starterFinalPerf: currentPerf,
                    });
                }
            }
        }
    });

    // Sort suggestions by improvement amount (best suggestions first)
    let sortedSuggestions = suggestions.sort((a, b) =>
        (b.reserveFinalPerf - b.starterFinalPerf) - (a.reserveFinalPerf - a.starterFinalPerf)
    );

    // Contextual suppression: Don't show suggestions in certain situations
    if (gameState) {
        // Check if there are key players returning from injury
        // If so, always show suggestions to get them back into the team
        const keyPlayersReturning = suggestions.some(s => {
            // A key player returning from injury would be:
            // - The reserve player in a suggestion (being recommended)
            // - Has good skill (>60)
            // - Wasn't in the last game (or few games)
            // - Current starter is significantly lower skill
            const reserveSkill = s.reserve.skill;
            const starterSkill = s.starter.skill;
            const significantUpgrade = reserveSkill > starterSkill + 5;

            return reserveSkill > 60 && significantUpgrade;
        });

        if (keyPlayersReturning) {
            // Always show suggestions for key players returning - they should get back in immediately
            return sortedSuggestions;
        }

        // 1. Check if team is performing well (winning streak or good league position)
        const league = gameState.leagues[gameState.playerLeagueName];
        if (league) {
            const playerTeamIndex = league.teams.findIndex((t: any) => t.name === team.name);
            const isInTopPositions = playerTeamIndex < 3; // Top 3 teams
            const playerTeam = league.teams[playerTeamIndex];

            // Check recent form - if last few matches were wins, suppress suggestions
            const recentWins = squad.reduce((sum, p) => {
                const recentForms = p.recentRatings ? p.recentRatings.slice(-3) : [];
                return sum + recentForms.filter(r => r >= 7).length; // Good recent ratings
            }, 0);
            const strongRecentForm = recentWins >= (squad.length * 0.6); // 60% of squad in good form

            if (isInTopPositions || strongRecentForm) {
                // Suppress all suggestions - team is doing well
                return [];
            }

            // 2. Check if same lineup was recently set (within last 2 weeks)
            // If most of current lineup has played recently, suppress suggestions
            const currentLineupPlayerIds = Object.values(lineup).filter(id => id !== null) as number[];
            const consistentPlayers = currentLineupPlayerIds.filter(id => {
                const player = squad.find(p => p.id === id);
                return player && getConsecutiveGameAppearances(player) >= 2;
            }).length;

            if (consistentPlayers / currentLineupPlayerIds.length > 0.7) {
                // 70%+ of lineup has played consistently - suppress suggestions
                return [];
            }
        }
    }

    return sortedSuggestions;
};

export const updateAITrainingFocus = (team: TeamInLeague): void => {
    // Reset individual player training (AI uses team-wide or positional focus only)
    team.squad.forEach(player => {
        player.trainingFocus = null;
    });

    // Determine team-wide training focus based on squad needs
    const avgFitness = team.squad.reduce((sum, p) => sum + p.fitness, 0) / team.squad.length;
    const tiredPlayers = team.squad.filter(p => p.fitness < 60).length;
    const avgAge = team.squad.reduce((sum, p) => sum + p.age, 0) / team.squad.length;
    const youngPlayers = team.squad.filter(p => p.age < 24 && p.skill < p.potential - 5).length;

    // If many tired players, rest the whole squad
    if (tiredPlayers > 6 || avgFitness < 65) {
        team.trainingFocus = 'Rest';
        return;
    }

    // If squad is young and underdeveloped, focus on fitness for long-term gains
    if (avgAge < 25 && youngPlayers > 5) {
        team.trainingFocus = 'Fitness';
        return;
    }

    // Otherwise use general training
    team.trainingFocus = 'General';

    // Set positional training for positions with most young talent
    const positionYoungTalent: Record<string, number> = {
        'GK': 0, 'DEF': 0, 'MID': 0, 'FWD': 0
    };

    team.squad.forEach(p => {
        if (p.age < 24 && p.skill < p.potential - 5) {
            positionYoungTalent[p.position]++;
        }
    });

    // Apply positional training to positions with most young talent
    const posTrainingMap: Record<string, TrainingFocus> = {
        'GK': 'Goalkeeping',
        'DEF': 'Defending',
        'MID': 'Midfield',
        'FWD': 'Attacking'
    };

    Object.keys(positionYoungTalent).forEach(pos => {
        if (positionYoungTalent[pos] >= 3) {
            team.positionalTrainingFocus[pos as 'GK' | 'DEF' | 'MID' | 'FWD'] = posTrainingMap[pos];
        } else {
            team.positionalTrainingFocus[pos as 'GK' | 'DEF' | 'MID' | 'FWD'] = null;
        }
    });

    // Rest individual tired starters
    const starterIds = new Set(Object.values(team.tactics.lineup).filter(id => id !== null));
    team.squad.forEach(player => {
        if (starterIds.has(player.id) && player.fitness < 50) {
            player.trainingFocus = 'Rest';
        }
    });
};